import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Award,
  FileSpreadsheet
} from "lucide-react";

import {
  getEligibleSchedulesForMarks,
  createMarksAssignment
} from "../../api/markschedule/marksApi";
import {
  getEligibleSchedulesForMarksPortal,
  createMarksAssignmentPortal
} from "../../api/markschedule/marksPortalApi";
import {
  getAllSections
} from "../../api/student/studentApi";
import {
  getAllSectionsForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getActiveAcademicYear
} from "../../api/academic/academicYearApi";
import {
  getAllAcademicYearsPortal,
  getActiveAcademicYearPortal
} from "../../api/academic/academicYearPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import EligibleScheduleCard from "../../components/markschedule/EligibleScheduleCard";
import {
  pickList,
  toId
} from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const resolveYearName = (res) => {
  if (!res) return "";
  const y = res.academicYear || res.data || res;
  if (typeof y === "string") return y.trim();
  return (y.yearName || y.name || y.academicYear || y.label || "")
    .toString()
    .trim();
};

const EligibleExamsPage = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canAssign = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearReady, setYearReady] = useState(false);
  const [academicYear, setAcademicYear] = useState("");
  const [years, setYears] = useState([]);
  const [assigningId, setAssigningId] = useState(null);

  const [sectionBySchedule, setSectionBySchedule] = useState({});
  const [staffBySchedule, setStaffBySchedule] = useState({});
  const [sectionsByClass, setSectionsByClass] = useState({});

  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allRes, activeRes] = await Promise.all([
          isPortal
            ? getAllAcademicYearsPortal({})
            : getAllAcademicYears({}),
          isPortal
            ? getActiveAcademicYearPortal()
            : getActiveAcademicYear(),
        ]);
        if (cancelled) return;
        const list = pickList(allRes, ["academicYears", "years", "data"]);
        const names = list
          .map((y) =>
            typeof y === "string"
              ? y
              : y.yearName || y.name || y.academicYear || ""
          )
          .filter(Boolean);
        const active = resolveYearName(activeRes);
        const yearSet = new Set(names);
        if (active) yearSet.add(active);
        setYears([...yearSet]);
        setAcademicYear(active || names[0] || "");
      } catch (e) {
        // non-fatal
      } finally {
        if (!cancelled) setYearReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const load = useCallback(async () => {
    if (!yearReady) return;
    try {
      setLoading(true);
      const res = isPortal
        ? await getEligibleSchedulesForMarksPortal({
            academicYear: academicYear || undefined,
          })
        : await getEligibleSchedulesForMarks({
            academicYear: academicYear || undefined,
          });
      setItems(pickList(res, ["eligible", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load eligible exams");
    } finally {
      setLoading(false);
    }
  }, [isPortal, academicYear, yearReady]);

  useEffect(() => {
    load();
  }, [load]);

  const ensureSections = async (classId) => {
    if (!classId || sectionsByClass[classId]) return;
    try {
      const res = isPortal
        ? await getAllSectionsForPortal(classId)
        : await getAllSections(classId);
      const list = pickList(res, ["sections", "data"]);
      setSectionsByClass((prev) => ({ ...prev, [classId]: list }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleSectionChange = (scheduleId, sectionId) => {
    setSectionBySchedule((prev) => ({ ...prev, [scheduleId]: sectionId }));
    setStaffBySchedule((prev) => ({ ...prev, [scheduleId]: "" }));
  };

  const handleAssign = async (scheduleId) => {
    const sectionId = sectionBySchedule[scheduleId];
    const assignedStaffId = staffBySchedule[scheduleId];
    if (!sectionId || !assignedStaffId) return;
    try {
      setAssigningId(scheduleId);
      const payload = { examScheduleId: scheduleId, sectionId, assignedStaffId };
      if (isPortal) await createMarksAssignmentPortal(payload);
      else await createMarksAssignment(payload);
      toast.success("Marks assignment created");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Assign failed");
    } finally {
      setAssigningId(null);
    }
  };

  const yearOptions = useMemo(
    () => [
      { label: "All years", value: "" },
      ...years.map((y) => ({ label: y, value: y })),
    ],
    [years]
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Marks Assignment
          </h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            Parent exam COMPLETED, schedule on/before today (auto-completed). Teacher from TeacherSubjectAssignment for subject + section.
          </p>
        </div>
        {isPortal && (
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => navigate("/staff-panel/marks/my-assignments")}
          >
            My Assignments
          </Button>
        )}
      </div>

      {/* Filter Card */}
      <Card>
        <CardContent className="p-4 space-y-1">
          <div className="w-full sm:w-64">
            <Select
              label="Academic Year"
              options={yearOptions}
              value={academicYear}
              onChange={(val) => setAcademicYear(val)}
            />
          </div>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            Defaults to the active academic year
          </p>
        </CardContent>
      </Card>

      {/* Alerts */}
      
      

      {/* Content */}
      {loading || !yearReady ? (
        <div className="space-y-4">
          <Skeleton height={120} width="100%" />
          <Skeleton height={120} width="100%" />
          <Skeleton height={120} width="100%" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={FileSpreadsheet}
              title="No eligible schedules"
              description="Ensure the exam is COMPLETED, has at least one subject schedule with max marks, and the schedule date is today or earlier."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const schedule = item.schedule || item;
            const sid = toId(schedule);
            const classId = toId(schedule.classId);
            if (classId && !sectionsByClass[classId]) {
              ensureSections(classId);
            }
            return (
              <EligibleScheduleCard
                key={sid}
                item={item}
                sections={sectionsByClass[classId] || []}
                selectedSectionId={sectionBySchedule[sid]}
                selectedStaffId={staffBySchedule[sid]}
                onSectionChange={(secId) => handleSectionChange(sid, secId)}
                onStaffChange={(staffId) =>
                  setStaffBySchedule((prev) => ({
                    ...prev,
                    [sid]: staffId,
                  }))
                }
                onAssign={handleAssign}
                assigning={assigningId === sid}
                canAssign={canAssign}
                isPortal={isPortal}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EligibleExamsPage;